import React, { useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, IconButton, Tooltip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { QRCodeSVG } from 'qrcode.react';
import { useDataProvider, useNotify } from 'react-admin';

import resourceNames from '../../constants/resourceNames';

type PreviewSession = {
  previewToken: string;
  previewUrl: string;
  expiresInSeconds: number;
};

type TestLink = {
  url: string;
  expiresInMinutes: number;
};

const TestLinkButtons = ({ formId }: { formId: string }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [testLink, setTestLink] = useState<TestLink | null>(null);
  const [creating, setCreating] = useState(false);

  const createTestLink = async (): Promise<TestLink> => {
    const { data } = await dataProvider.create<PreviewSession & { id: string }>(
      `${resourceNames.FORMS}/${formId}/preview-session`,
      { data: {} },
    );
    const url = new URL(data.previewUrl);
    // Only the Studio frame needs the form app's frame headers dropped.
    url.searchParams.delete('iframe');
    url.hash = `previewToken=${encodeURIComponent(data.previewToken)}`;
    return { url: url.toString(), expiresInMinutes: Math.round(data.expiresInSeconds / 60) };
  };

  const copyTestLink = async (): Promise<void> => {
    setCreating(true);
    const link = createTestLink();
    try {
      // Safari only allows the write during the click, so it starts before the link exists.
      await navigator.clipboard.write([new ClipboardItem({
        'text/plain': link.then(({ url }) => new Blob([url], { type: 'text/plain' })),
      })]);
      const { expiresInMinutes } = await link;
      notify(
        `Test link copied. Open it within ${expiresInMinutes} minutes; submitting it sends no data.`,
        { type: 'success' },
      );
    } catch {
      notify('The test link could not be copied.', { type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const showTestLinkQrCode = async (): Promise<void> => {
    setCreating(true);
    try {
      setTestLink(await createTestLink());
    } catch {
      notify('The test link could not be created.', { type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Tooltip title="Copy test link">
        <span>
          <IconButton
            size="small"
            aria-label="Copy test link"
            disabled={creating}
            onClick={() => { copyTestLink(); }}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Show test link QR code">
        <span>
          <IconButton
            size="small"
            aria-label="Show test link QR code"
            disabled={creating}
            onClick={() => { showTestLinkQrCode(); }}
          >
            <QrCodeIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={testLink !== null} onClose={() => setTestLink(null)}>
        <DialogContent>
          {testLink && <QRCodeSVG value={testLink.url} size={240} marginSize={2} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestLink(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TestLinkButtons;
